import "./sentry";
import "./posthog";

import { lazy, Suspense, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { AppErrorFallback } from "@bitsentry/components/ErrorFallback";
import { registerDashboardTranslations } from "@bitsentry/i18n";
import { I18nProvider } from "@bitsentry-ce/i18n";
import App from "./App.tsx";
import FrontendSentryTestTrigger from "./components/FrontendSentryTestTrigger";
import { queryClient } from "./platform/query/client";
import "@radix-ui/themes/styles.css";
import "@workos-inc/widgets/styles.css";
import "./index.css";

registerDashboardTranslations();

const SentryApp = Sentry.withProfiler(App) as typeof App;

// Agentation is a development-only overlay; keep production builds from resolving it.
let AgentationDevOverlay: ReturnType<typeof lazy> | null = null;
if (import.meta.env.DEV) {
  AgentationDevOverlay = lazy(async () => {
    const { Agentation } = await import("agentation");
    return { default: Agentation };
  });
}

const rootElement = document.getElementById("root");

if (rootElement === null) {
  throw new Error("Root element not found");
}

const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(async () => ({
      default: (await import("@tanstack/react-query-devtools"))
        .ReactQueryDevtools,
    }))
  : null;
let reactQueryDevtools: ReactNode = null;
if (ReactQueryDevtools !== null) {
  reactQueryDevtools = (
    <Suspense fallback={null}>
      <ReactQueryDevtools initialIsOpen={false} />
    </Suspense>
  );
}

let agentationOverlay: ReactNode = null;
if (AgentationDevOverlay !== null) {
  agentationOverlay = (
    <Suspense fallback={null}>
      <AgentationDevOverlay endpoint="http://localhost:4747" />
    </Suspense>
  );
}

createRoot(rootElement).render(
  <QueryClientProvider client={queryClient}>
    <I18nProvider>
      <Sentry.ErrorBoundary fallback={<AppErrorFallback />}>
        <FrontendSentryTestTrigger />
        <SentryApp />
      </Sentry.ErrorBoundary>
      {reactQueryDevtools}
      {agentationOverlay}
    </I18nProvider>
  </QueryClientProvider>,
);
